import React, { useEffect, useRef } from "react";
import { Table, Button, Row, Col, Card } from "react-bootstrap";
import { format, parseISO } from "date-fns";
import SortableHeader from "./SortableHeader";
import * as ROUTES from "../../../Constants/Routes";
import STRINGS from "../../../Constants/Strings";

/* Personal Members Search Results Table Component */
const PersonalMembersTable = ({
  searchResults,
  isLoading,
  hasSearched,
  pagination,
  setPagination,
  sortConfig,
  handleSort,
  handleViewMember,
  history,
  onRefresh,
}) => {
  const tableHeaderRef = useRef(null);
  useEffect(() => {
    if (hasSearched && tableHeaderRef.current) {
      const offset = 16 * 16; // 16em sticky header padding offset but will need to make this something we can drive from outside the hardcode here.
      const top =
        tableHeaderRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: top - offset,
        behavior: "smooth",
      });
    }
  }, [hasSearched, searchResults.length]);

  return (
    <>
      {/* BEGIN - PERSONAL MEMBERS TABLE WIDGET */}
      <div className="[ row ]">
        <div className="[ col-xl-12 col-lg-12 ] [ main-container__members-column ]">
          <section
            className="branch-widget branch-widget--members"
            aria-labelledby="personal-members-section-title"
          >
            <Card className="branch-widget__card">
              <Card.Body>
                {/* Header */}
                <div className="branch-widget__header">
                  <div className="branch-widget__header-left">
                    <i
                      className="fas fa-users branch-widget__icon"
                      aria-hidden="true"
                    ></i>
                    <h2
                      id="personal-members-section-title"
                      className="branch-widget__title"
                    >
                      {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.TITLE}
                    </h2>
                  </div>
                </div>

                {/* Subheader / Table Header */}
                <div className="branch-widget__table-header">
                  <div className="row">
                    <div className="[ col-12 ] [ d-flex justify-content-between align-items-center flex-wrap ]">
                      <span
                        ref={tableHeaderRef}
                        className="branch-widget__table-title"
                        aria-live="polite"
                      >
                        {!hasSearched
                          ? ""
                          : searchResults.length > 0
                          ? STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .DISPLAYING_RESULTS
                          : STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .NO_MATCHING}
                      </span>
                      {hasSearched && (
                        <Button
                          variant="light"
                          size="sm"
                          className="branch-widget__refresh branch-widget__refresh--icon"
                          aria-label={
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .REFRESH_ARIA_LABEL
                          }
                          onClick={onRefresh}
                        >
                          <i className="fas fa-sync" aria-hidden="true"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info Banner */}
                {searchResults.length > 0 && (
                  <div className="branch-widget__banner branch-widget__banner--info">
                    <p className="branch-widget__banner-text">
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .BANNER_INFO
                      }
                    </p>
                  </div>
                )}

                {/* Pagination - top */}
                <div className="branch-widget__pagination d-flex align-items-center justify-content-between">
                  <div className="branch-widget__pagination-left d-flex align-items-center">
                    <label
                      htmlFor="itemsPerPage-top"
                      className="branch-widget__pagination-label"
                    >
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .ITEMS_PER_PAGE
                      }
                    </label>
                    <select
                      id="itemsPerPage-top"
                      className="branch-widget__pagination-select form-select form-select-sm"
                      value={pagination.pageSize}
                      onChange={(e) => {
                        const newPageSize = parseInt(e.target.value, 10);
                        setPagination((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                          currentPage: 0,
                        }));
                      }}
                    >
                      <option value={20}>20</option>
                      <option value={40}>40</option>
                    </select>
                  </div>
                  <div className="branch-widget__pagination-right d-flex align-items-center">
                    <label
                      htmlFor="pageSelect-top"
                      className="branch-widget__pagination-label"
                    >
                      {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PAGE}
                    </label>
                    <select
                      id="pageSelect-top"
                      className="branch-widget__pagination-select form-select form-select-sm"
                      value={pagination.currentPage + 1}
                      onChange={(e) => {
                        const newPage = parseInt(e.target.value, 10) - 1;
                        setPagination((prev) => ({
                          ...prev,
                          currentPage: newPage,
                        }));
                      }}
                    >
                      {Array.from(
                        { length: Math.max(1, pagination.totalPages) },
                        (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        )
                      )}
                    </select>
                    <span className="branch-widget__pagination-info">
                      {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PAGE_OF}{" "}
                      {pagination.totalPages || 1}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .PREV_PAGE
                      }
                      className="branch-widget__pagination-btn"
                      disabled={pagination.currentPage === 0}
                      onClick={() => {
                        if (pagination.currentPage > 0) {
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: prev.currentPage - 1,
                          }));
                        }
                      }}
                    >
                      <i className="fas fa-chevron-left" aria-hidden="true"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .NEXT_PAGE
                      }
                      className="branch-widget__pagination-btn"
                      disabled={
                        pagination.currentPage >= pagination.totalPages - 1
                      }
                      onClick={() => {
                        if (
                          pagination.currentPage <
                          pagination.totalPages - 1
                        ) {
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: prev.currentPage + 1,
                          }));
                        }
                      }}
                    >
                      <i
                        className="fas fa-chevron-right"
                        aria-hidden="true"
                      ></i>
                    </Button>
                  </div>
                </div>

                {/* Table */}
                <Row>
                  <Col xs={12}>
                    <Table
                      striped
                      hover
                      size="sm"
                      responsive
                      className="branch-widget__table"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .ARIA_LABEL
                      }
                    >
                      <thead>
                        <tr>
                          <SortableHeader
                            field="firstName"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_FIRSTNAME
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            field="middleName"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_MIDDLENAME
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            field="lastName"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_LASTNAME
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            field="dateOfBirth"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_DOB
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            field="sin"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_SSN
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            field="memberNumber"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_MEMBERID
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            field="email"
                            label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_EMAIL
                            }
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <th
                            scope="col"
                            aria-label={
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                                .TH_VIEW
                            }
                          >
                            <i
                              className="fas fa-external-link-alt"
                              aria-hidden="true"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td
                              colSpan={8}
                              className="text-center text-muted py-4"
                            >
                              <div
                                role="status"
                                aria-live="polite"
                                className="branch-widget__status branch-widget__status--loading"
                              >
                                <i
                                  className="fas fa-spinner fa-spin mr-2"
                                  aria-hidden="true"
                                ></i>
                                {
                                  STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                    .TABLE.STATUS_SEARCHING
                                }
                              </div>
                            </td>
                          </tr>
                        ) : hasSearched && searchResults.length > 0 ? (
                          searchResults.map((result, idx) => (
                            <tr key={result.id || idx}>
                              <td>{result.firstName || ""}</td>
                              <td>{result.middleName || ""}</td>
                              <td>{result.lastName || ""}</td>
                              <td>
                                {result.dateOfBirth
                                  ? format(
                                      parseISO(result.dateOfBirth),
                                      "MM-dd-yyyy"
                                    )
                                  : ""}
                              </td>
                              <td>
                                {result.sin
                                  ? `***-**-${result.sin.slice(-4)}`
                                  : ""}
                              </td>
                              <td>{result.memberNumber || ""}</td>
                              <td>{result.email || ""}</td>
                              <td className="branch-widget__body-link">
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewMember(
                                      result.accountId,
                                      null,
                                      result.applicantId,
                                      result
                                    );
                                  }}
                                  aria-label={`${
                                    STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                      .TABLE.LINK_VIEW
                                  } ${result.firstName || "member"}`}
                                >
                                  <i
                                    className="fas fa-external-link-alt"
                                    aria-hidden="true"
                                  />
                                </a>
                              </td>
                            </tr>
                          ))
                        ) : !hasSearched ? (
                          <tr>
                            <td
                              colSpan={8}
                              className="text-center text-muted py-4"
                            >
                              <div className="branch-widget__message branch-widget__message--empty">
                                <div>
                                  {
                                    STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                      .TABLE.EMPTY_LINE1
                                  }
                                </div>
                                <div>
                                  {
                                    STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS
                                      .TABLE.EMPTY_LINE2
                                  }
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                {/* Pagination - bottom */}
                {searchResults.length > 19 && (
                  <div className="branch-widget__pagination d-flex align-items-center justify-content-between">
                    {/* identical to top pagination */}
                    <div className="branch-widget__pagination-left d-flex align-items-center">
                      <label
                        htmlFor="itemsPerPage-bottom"
                        className="branch-widget__pagination-label"
                      >
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .ITEMS_PER_PAGE
                        }
                      </label>
                      <select
                        id="itemsPerPage-bottom"
                        className="branch-widget__pagination-select form-select form-select-sm"
                        value={pagination.pageSize}
                        onChange={(e) => {
                          const newPageSize = parseInt(e.target.value, 10);
                          setPagination((prev) => ({
                            ...prev,
                            pageSize: newPageSize,
                            currentPage: 0,
                          }));
                        }}
                      >
                        <option value={20}>20</option>
                        <option value={40}>40</option>
                      </select>
                    </div>
                    <div className="branch-widget__pagination-right d-flex align-items-center">
                      <label
                        htmlFor="pageSelect-bottom"
                        className="branch-widget__pagination-label"
                      >
                        {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PAGE}
                      </label>
                      <select
                        id="pageSelect-bottom"
                        className="branch-widget__pagination-select form-select form-select-sm"
                        value={pagination.currentPage + 1}
                        onChange={(e) => {
                          const newPage = parseInt(e.target.value, 10) - 1;
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: newPage,
                          }));
                        }}
                      >
                        {Array.from(
                          { length: Math.max(1, pagination.totalPages) },
                          (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          )
                        )}
                      </select>
                      <span className="branch-widget__pagination-info">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .PAGE_OF
                        }{" "}
                        {pagination.totalPages || 1}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label={
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .PREV_PAGE
                        }
                        className="branch-widget__pagination-btn"
                        disabled={pagination.currentPage === 0}
                        onClick={() => {
                          if (pagination.currentPage > 0) {
                            setPagination((prev) => ({
                              ...prev,
                              currentPage: prev.currentPage - 1,
                            }));
                          }
                        }}
                      >
                        <i
                          className="fas fa-chevron-left"
                          aria-hidden="true"
                        ></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label={
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .NEXT_PAGE
                        }
                        className="branch-widget__pagination-btn"
                        disabled={
                          pagination.currentPage >= pagination.totalPages - 1
                        }
                        onClick={() => {
                          if (
                            pagination.currentPage <
                            pagination.totalPages - 1
                          ) {
                            setPagination((prev) => ({
                              ...prev,
                              currentPage: prev.currentPage + 1,
                            }));
                          }
                        }}
                      >
                        <i
                          className="fas fa-chevron-right"
                          aria-hidden="true"
                        ></i>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Bottom Banner */}
                {hasSearched && (
                  <div className="branch-widget__banner branch-widget__banner--bottom">
                    {searchResults.length > 0 ? (
                      <>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .BANNER_BOTTOM_REVIEW
                          }
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="[ cta__primary-action-call btn info-btn ]"
                          onClick={() =>
                            history.push(ROUTES.SMART_BRANCH_REGISTER)
                          }
                        >
                          <i
                            className="fas fa-user-plus mr-2"
                            aria-hidden="true"
                          ></i>
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .BANNER_BOTTOM_ADD
                          }
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .BANNER_BOTTOM_NO_RESULTS1
                          }
                        </div>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .BANNER_BOTTOM_NO_RESULTS2
                          }
                        </div>
                        <div className="branch-widget__banner-subtext">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .BANNER_BOTTOM_IF_NOT_MEMBER
                          }
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="[ cta__primary-action-call btn info-btn ]"
                          onClick={() =>
                            history.push(ROUTES.SMART_BRANCH_REGISTER)
                          }
                        >
                          <i
                            className="fas fa-user-plus mr-2"
                            aria-hidden="true"
                          ></i>
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                              .BANNER_BOTTOM_ADD
                          }
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </section>
        </div>
      </div>
      {/* END - PERSONAL MEMBERS TABLE WIDGET */}
    </>
  );
};

export default PersonalMembersTable;
